import { useState, useEffect } from "react";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	listSprints,
	updateSprint,
	deleteSprint,
	type Sprint,
} from "@/services/sprint.service";
import { toast } from "sonner";
import { SprintsListSkeleton } from "@/components/sprints/SprintsListSkeleton";
import { EditSprintDialog } from "@/components/sprints/EditSprintDialog";
import { GenerateSprintsDialog } from "@/components/sprints/GenerateSprintsDialog";
import { SprintFilters, type SprintView } from "@/components/sprints/SprintFilters";
import { SprintsList } from "@/components/sprints/SprintsList";
import { SprintsTable } from "@/components/sprints/SprintsTable";
import { SprintsPagination } from "@/components/sprints/SprintsPagination";
import { useSprintsFilter } from "@/components/sprints/useSprintsFilter";
import { usePermission } from "@/hooks/usePermission";

const VIEW_KEY = "sprints:view";
const GENERATE_ROLES = new Set(["admin", "manager"]);

function readView(): SprintView {
	try {
		return localStorage.getItem(VIEW_KEY) === "table" ? "table" : "list";
	} catch {
		return "list";
	}
}

function byStartDate(a: Sprint, b: Sprint) {
	return a.start_date.localeCompare(b.start_date);
}

export default function SprintsPage() {
	const [sprints, setSprints] = useState<Sprint[]>([]);
	const [loading, setLoading] = useState(true);
	const [generateOpen, setGenerateOpen] = useState(false);
	const [editSprint, setEditSprint] = useState<Sprint | null>(null);
	const [deletingId, setDeletingId] = useState<string | null>(null);
	const [updatingId, setUpdatingId] = useState<string | null>(null);
	const [view, setView] = useState<SprintView>(readView);
	const filter = useSprintsFilter(sprints);
	const { roleKey } = usePermission();
	const canGenerate = !!roleKey && GENERATE_ROLES.has(roleKey);

	useEffect(() => {
		listSprints()
			.then((data) => setSprints(data))
			.catch(() => toast.error("Failed to load sprints."))
			.finally(() => setLoading(false));
	}, []);

	function handleViewChange(next: SprintView) {
		setView(next);
		try {
			localStorage.setItem(VIEW_KEY, next);
		} catch {
			// ignore storage failures
		}
	}

	async function handleDelete(sprintId: string) {
		try {
			await deleteSprint(sprintId);
			setSprints((prev) => prev.filter((s) => s.id !== sprintId));
			setDeletingId(null);
			toast.success("Sprint deleted.");
		} catch {
			toast.error("Failed to delete sprint.");
		}
	}

	async function handleActivate(sprint: Sprint) {
		const alreadyActive = sprints.some((s) => s.status === "active");
		if (alreadyActive) {
			toast.error("Another sprint is already active.");
			return;
		}
		setUpdatingId(sprint.id);
		try {
			const updated = await updateSprint(sprint.id, { status: "active" });
			setSprints((prev) => prev.map((s) => (s.id === sprint.id ? updated : s)));
			toast.success("Sprint started.");
		} catch {
			toast.error("Failed to start sprint.");
		} finally {
			setUpdatingId(null);
		}
	}

	async function handleComplete(sprint: Sprint) {
		setUpdatingId(sprint.id);
		try {
			const updated = await updateSprint(sprint.id, { status: "completed" });
			setSprints((prev) => prev.map((s) => (s.id === sprint.id ? updated : s)));
			toast.success("Sprint ended.");
		} catch {
			toast.error("Failed to end sprint.");
		} finally {
			setUpdatingId(null);
		}
	}

	const rowHandlers = {
		deletingId,
		updatingId,
		onActivate: handleActivate,
		onComplete: handleComplete,
		onEdit: setEditSprint,
		onAskDelete: setDeletingId,
		onDelete: handleDelete,
	};

	const generateButton = canGenerate && (
		<Button className="flex w-full items-center gap-2 sm:w-auto" onClick={() => setGenerateOpen(true)}>
			<Sparkles className="h-4 w-4" />
			Auto generate
		</Button>
	);

	return (
		<div className="mx-auto max-w-[1280px] px-4 py-8 sm:px-6">
			{/* Header */}
			<div className="flex flex-col gap-4 mb-8 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h1 className="text-2xl font-semibold text-foreground tracking-tight">
						Sprints
					</h1>
					<p className="text-sm text-muted mt-1">
						Org-level sprints span one work week (Mon - Fri). Assign projects to a sprint from the project page.
					</p>
				</div>
				{generateButton}
			</div>

			{/* Content */}
			{loading ? (
				<SprintsListSkeleton />
			) : sprints.length === 0 ? (
				<Card className="p-12 text-center">
					<p className="text-sm font-medium text-foreground mb-1">No sprints yet</p>
					<p className="text-sm text-muted mb-4">
						{canGenerate
							? "Auto generate weekly sprints to start organizing projects by week."
							: "An admin or manager can generate weekly sprints."}
					</p>
					{generateButton}
				</Card>
			) : (
				<>
					<SprintFilters
						search={filter.search}
						onSearchChange={filter.setSearch}
						status={filter.status}
						onStatusChange={filter.setStatus}
						sort={filter.sort}
						onSortChange={filter.setSort}
						view={view}
						onViewChange={handleViewChange}
					/>
					{filter.filtered.length === 0 ? (
						<Card className="p-12 text-center">
							<p className="text-sm text-muted">No sprints match your filters.</p>
						</Card>
					) : (
						<>
							{view === "table" ? (
								<SprintsTable
									sprints={filter.paged}
									sort={filter.sort}
									onSort={filter.toggleSort}
									{...rowHandlers}
								/>
							) : (
								<SprintsList sprints={filter.paged} {...rowHandlers} />
							)}
							<SprintsPagination
								page={filter.page}
								totalPages={filter.totalPages}
								total={filter.filtered.length}
								onPageChange={filter.setPage}
							/>
						</>
					)}
				</>
			)}

			{/* Dialogs */}
			<GenerateSprintsDialog
				open={generateOpen && canGenerate}
				onClose={() => setGenerateOpen(false)}
				onGenerated={(created) =>
					setSprints((prev) => [...prev, ...created].sort(byStartDate))
				}
			/>

			{editSprint && (
				<EditSprintDialog
					open={!!editSprint}
					onClose={() => setEditSprint(null)}
					sprint={editSprint}
					onSaved={(updated) => {
						setSprints((prev) =>
							prev.map((s) => (s.id === updated.id ? updated : s)).sort(byStartDate),
						);
						setEditSprint(null);
					}}
				/>
			)}
		</div>
	);
}
